import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

interface EpcQrCodeProps {
  iban: string;
  bic?: string;
  accountHolder: string;
  amount: number;
  invoiceNumber: string;
  currency?: string;
  /** BT-83: payment reference (Verwendungszweck). Takes priority over invoiceNumber in remittance line. */
  paymentReference?: string;
}

/**
 * Renders a GiroCode / EPC QR code (SEPA Credit Transfer).
 * Compatible with all German banking apps (Sparkasse, ING, DKB, etc.)
 *
 * Format: https://en.wikipedia.org/wiki/EPC_QR_code
 */
const EpcQrCode: React.FC<EpcQrCodeProps> = ({
  iban,
  bic = '',
  accountHolder,
  amount,
  invoiceNumber,
  currency = 'EUR',
  paymentReference,
}) => {
  const { t } = useTranslation('common');

  // EPC / GiroCode payload (SEPA Credit Transfer)
  // Line 1: BCD          Service Tag
  // Line 2: 001          Version
  // Line 3: 1            Character set (UTF-8)
  // Line 4: SCT          Identification code
  // Line 5: BIC          (may be empty)
  // Line 6: Creditor name
  // Line 7: IBAN
  // Line 8: Amount       EUR{amount}
  // Line 9: Purpose      (empty)
  // Line 10: Structured reference (empty)
  // Line 11: Unstructured remittance info (invoice number)
  const amountFormatted = currency + amount.toFixed(2);
  const creditorName = accountHolder.substring(0, 70);
  const remittance = (paymentReference || invoiceNumber).substring(0, 140);

  const epcPayload = [
    'BCD',
    '001',
    '1',
    'SCT',
    bic,
    creditorName,
    iban.replace(/\s/g, ''),
    amountFormatted,
    '',
    '',
    remittance,
  ].join('\n');

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="bg-white p-3 rounded-lg">
        <QRCodeSVG
          value={epcPayload}
          size={140}
          level="M"
          includeMargin={false}
        />
      </div>
      <p className="text-xs text-muted text-center">
        {t('receivedInvoices.qrCodeLabel', 'Banking-App scannen')}
      </p>
    </div>
  );
};

export default EpcQrCode;
